import type React from 'react'
import AppButton from '../../../../components/atoms/AppButton/AppButton'
import AppInput from '../../../../components/atoms/AppInput/AppInput'
import AppSelect from '../../../../components/atoms/AppSelect/AppSelect'
import useCompanyForm from '../../hooks/useCompanyForm'
import useCountry from '../../hooks/useCountrys'
import type { CreateCompanyUseCase } from '../../../usecases/CreateCompanyUseCase'
import { useRef, useState } from 'react'
import { Snackbar } from '../../../../components/atoms/Snackbar/Snackbar'

interface Props {
    createCompanyUseCase: CreateCompanyUseCase
    onSuccess?: () => void
    onCreated?: (company: unknown) => void
}

interface SnackbarHandle {
    showModal: (value: boolean) => void
}

const CompanyForm: React.FC<Props> = ({
    createCompanyUseCase,
    onSuccess,
    onCreated,
}: Props) => {
    const { handleChange, handleSubmit, formData, isFormValid, isLoading } =
        useCompanyForm({ createCompanyUseCase, onSuccess })

    const {
        countries,
        isLoading: countriesLoading,
        error: countriesError,
    } = useCountry()
    const snackbarRef = useRef<SnackbarHandle>(null)
    const [snackbarMessage, setSnackbarMessage] = useState<{
        title: string
        description: string
        state: 'success' | 'error'
    }>({
        title: 'Éxito',
        description: 'Acción completada',
        state: 'success',
    })

    const countryOptions = countries.map((country) => ({
        label: country.name,
        value: country.id,
    }))

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        handleChange(e)
    }

    const handleForm = async (form: React.FormEvent) => {
        form.preventDefault()
        try {
            const companyCreated = await handleSubmit(form)
            if (onCreated && companyCreated) {
                onCreated(companyCreated)
                setSnackbarMessage({
                    title: '¡Éxito!',
                    description: 'Empresa creada correctamente',
                    state: 'success',
                })
                snackbarRef.current?.showModal(true)
            }
        } catch (e) {
            const errorMessage =
                e instanceof Error ? e.message : 'Error al crear la empresa'
            setSnackbarMessage({
                title: 'Error',
                description: errorMessage,
                state: 'error',
            })
            snackbarRef.current?.showModal(true)
        }
    }

    return (
        <form onSubmit={handleForm}>
            <div className="mb-5">
                <h2 className="text-2xl font-bold text-gray-800">
                    Nueva empresa
                </h2>
                <p className="text-gray-600 mt-1">
                    Complete el formulario con los datos correctos de la nueva
                    empresa sin dejar campos vacíos.
                </p>
                <Snackbar
                    ref={snackbarRef}
                    title={snackbarMessage.title}
                    description={snackbarMessage.description}
                    state={snackbarMessage.state}
                    closeAction
                    time={2000}
                />

                {countriesError !== null && countriesError !== undefined && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                        <p className="text-yellow-700 font-medium text-sm">
                            {countriesError}
                        </p>
                    </div>
                )}

                {countriesError !== null && countriesError !== undefined && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                        <p className="text-yellow-700 font-medium text-sm">
                            {countriesError}
                        </p>
                    </div>
                )}
            </div>

            <fieldset className="flex flex-col gap-4 mb-5" disabled={isLoading}>
                <legend className="text-lg font-semibold text-gray-700 mb-5 w-full">
                    Identidad
                </legend>
                <div className="w-full md:w-1/2">
                    <AppInput
                        label={'Nombre Comercial'}
                        name="commercialName"
                        value={formData.commercialName}
                        onChange={handleInputChange}
                        placeholder="Ej. Mi Empresa S.A."
                        required
                    />
                </div>
            </fieldset>

            <fieldset
                className="flex flex-col gap-4 md:flex-row md:gap-4 mb-5"
                disabled={isLoading || countriesLoading}
            >
                <legend className="text-lg font-semibold text-gray-700 mb-5 w-full">
                    Ubicación legal
                </legend>
                <div className="w-full md:w-1/2">
                    <AppSelect
                        name="country"
                        options={countryOptions}
                        onChange={handleInputChange}
                        label={countriesLoading ? 'Cargando países...' : 'País'}
                        required
                        className="mt-1 text-sm"
                        disabled={countriesLoading}
                    />
                </div>

                <div className="w-full md:w-1/2">
                    <AppInput
                        label={'Dirección Fiscal'}
                        name="fiscalAddress"
                        value={formData.fiscalAddress}
                        onChange={handleInputChange}
                        placeholder="Ej. Av. Reforma 123"
                        required
                    />
                </div>
            </fieldset>

            <div className="mt-6 flex gap-3">
                <AppButton
                    title={isLoading ? 'Creando...' : 'Crear empresa'}
                    buttonType={'submit'}
                    disabled={
                        isFormValid === false || isLoading || countriesLoading
                    }
                    state={
                        isFormValid === false &&
                        isLoading === false &&
                        countriesLoading === false
                            ? 'primary'
                            : 'default'
                    }
                />
            </div>
        </form>
    )
}

export default CompanyForm
